import React, { useState, useEffect } from 'react'
import {
  RadialBarChart,
  RadialBar,
  Legend,
  PolarAngleAxis,
  Tooltip,
} from 'recharts'
import {
  ChartContainer,
  ChartTitle,
  StyledViewAllBtn,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  DetailedLegend,
  StyledWrapper,
} from './style'
import ExpendArrowIco from 'components/icons/ExpendArrowIco'

// Legend style
const legendStyle = {
  top: '50%',
  right: 20,
  transform: 'translateY(-50%)',
  lineHeight: '24px',
  fontSize: '12px',
  fontFamily: 'Plus Jakarta Sans',
}

// Custom legend icon renderer
const renderLegendIcon = (value, entry) => {
  const { color, payload } = entry

  return (
    <div
      style={{
        width: '40px',
        height: '20px',
        backgroundColor: payload.fill,
        borderRadius: '12px',
        border: `0.5px solid ${payload.stroke}`,
        display: 'inline-block',
        marginRight: '10px',
        verticalAlign: 'middle',
      }}
    />
  )
}

const CollegeChart = ({ chartMetrics }) => {
  // Remove focus outline from recharts components
  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `.recharts-wrapper :focus { outline: none !important; box-shadow: none !important; }`
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  const dataTemplate = [
    {
      name: 'Tier 1',
      fill: '#FFE5ED',
      stroke: '#EE8FAE',
      tier: 1,
    },
    {
      name: 'Tier 2',
      fill: '#E7E0FF',
      stroke: '#B5A3F7',
      tier: 2,
    },
    {
      name: 'Tier 3',
      fill: '#E0F7FF',
      stroke: '#97CADB',
      tier: 3,
    },
    {
      name: 'Tier 4',
      fill: '#E0FFE5',
      stroke: '#97DBB3',
      tier: 4,
    },
  ]

  // chartMetrics should be passed as a prop
  // Calculate fullValue as the total sum of all values
  const fullValue = chartMetrics?.reduce(
    (sum, item) => sum + Number(item.value),
    0
  )

  // Map chartMetrics to data array
  const data = dataTemplate.map(d => {
    const metric = chartMetrics?.find(m => Number(m.name) === d.tier)
    return {
      ...d,
      colleges: metric ? Number(metric?.value) : 0,
      fullValue,
    }
  })

  const [showModal, setShowModal] = useState(false)

  return (
    <ChartContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <ChartTitle>
          <span>Tier-wise College Classification</span>
        </ChartTitle>
        <StyledViewAllBtn
          text="View"
          icon={<ExpendArrowIco />}
          height={'32px'}
          onClick={() => setShowModal(true)}
        />
      </div>

      <StyledWrapper>
        <RadialBarChart
          width={400}
          height={280}
          cx={120}
          cy={140}
          innerRadius={40}
          outerRadius={120}
          barSize={12}
          data={data}
          startAngle={90}
          endAngle={-270}
        >
          <PolarAngleAxis
            type="number"
            domain={[0, fullValue || 0]}
            angleAxisId={0}
            tick={false}
          />
          <RadialBar
            clockWise
            dataKey="colleges"
            cornerRadius={6}
            strokeWidth={0.5}
            stroke={data => data.stroke}
            //             startAngle={0}
            // endAngle={360}
            minAngle={15}
            label={false}
            background={{ fill: '#EEEEEE', opacity: 0.8 }}
            barGap={8}
            maxBarSize={12}
            // Using full values for complete circles
            data={data}
          />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length > 0) {
                const d = payload[0].payload
                return (
                  <div
                    style={{
                      background: '#fff',
                      border: '1px solid #ccc',
                      padding: 8,
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{d.name}</div>
                    <div>
                      <span style={{ fontWeight: 'bold' }}>{d.colleges}</span>{' '}
                      Colleges
                    </div>
                  </div>
                )
              }
              return null
            }}
          />
          <Legend
            iconSize={0}
            layout="vertical"
            verticalAlign="middle"
            align="right"
            wrapperStyle={legendStyle}
            formatter={(value, entry, index) =>
              `${value} (${data[index].colleges} Colleges)`
            }
            iconType="circle"
            content={props => {
              const { payload } = props

              return (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {payload.map((entry, index) => (
                    <li
                      key={`item-${index}`}
                      style={{
                        marginBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {renderLegendIcon(entry.value, entry)}
                      <span
                        style={{
                          fontSize: '12px',
                          fontFamily: 'Plus Jakarta Sans',
                          color: entry.payload.stroke,
                        }}
                      >
                        {entry.value}
                      </span>
                    </li>
                  ))}
                </ul>
              )
            }}
          />
        </RadialBarChart>
      </StyledWrapper>

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle fontSize="13px">
                Tier-wise College Classification
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>

            <div
              style={{
                display: 'flex',
                height: '500px',
                width: '100%',
                border: '1px solid #C3C3C3',
              }}
            >
              <div
                style={{
                  width: '65%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <RadialBarChart
                  width={500}
                  height={500}
                  cx={250}
                  cy={250}
                  innerRadius={60}
                  outerRadius={200}
                  barSize={15}
                  data={data}
                  startAngle={90}
                  endAngle={-270}
                >
                  <PolarAngleAxis
                    type="number"
                    domain={[0, fullValue]}
                    angleAxisId={0}
                    tick={false}
                  />
                  <RadialBar
                    minAngle={15}
                    cornerRadius={6}
                    strokeWidth={0.5}
                    background={{ fill: '#EEEEEE', opacity: 0.8 }}
                    dataKey="colleges"
                    stroke={data => data.stroke}
                    label={false}
                    clockWise
                  />
                  <Tooltip
                    content={({ payload }) => {
                      if (payload && payload.length > 0) {
                        const d = payload[0].payload
                        return (
                          <div
                            style={{
                              background: '#fff',
                              border: '1px solid #ccc',
                              padding: 8,
                              borderRadius: 4,
                            }}
                          >
                            <div style={{ fontWeight: 600 }}>{d.name}</div>
                            <div>
                              <span style={{ fontWeight: 'bold' }}>
                                {d.colleges}
                              </span>{' '}
                              Colleges
                            </div>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Legend iconSize={0} payload={[]} content={() => null} />
                </RadialBarChart>
              </div>
              <div
                style={{ width: '35%', display: 'flex', alignItems: 'center' }}
              >
                <DetailedLegend style={{ width: '100%' }}>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {data.map((item, index) => (
                      <li
                        key={`item-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 15,
                        }}
                      >
                        <span
                          style={{
                            width: 30,
                            height: 14,
                            borderRadius: 10,
                            backgroundColor: item.fill,
                            border: `1px solid ${item.stroke}`,
                            display: 'inline-block',
                            marginRight: 10,
                          }}
                        />
                        <span
                          style={{
                            color: item.stroke,
                            marginRight: 10,

                            fontSize: '16px',
                          }}
                        >
                          {item.name}:
                        </span>
                        <span style={{ fontSize: '15px' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            {item.colleges}
                          </span>{' '}
                          Colleges
                        </span>
                      </li>
                    ))}
                  </ul>
                </DetailedLegend>
              </div>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </ChartContainer>
  )
}

export { CollegeChart }
